/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import Grid from "@mui/material/Grid";
import { formValue } from "../../../services/formServiceFunctions";
import MenuItem from "@mui/material/MenuItem";
import useForm from "../../../hooks/useForm";
import { intFieldDefault, requiredFieldDefault, textFieldDefault } from "../../../services/validationRules";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import ValidatorForm from "../../Validator/ValidatorForm";
import SubmitButton from "../../Button/SubmitButton";
import _ from "lodash";
import ValidatorNumberField from "../../Validator/ValidatorNumberField";
import { Box } from "@mui/material";
import { processCostSupportStyle } from "../processCostSupportStyle";
import { ProcessCostSupportFormProps } from "../../../types/ProcessCostSupport";

const RealEstate = ({ pcs, onSubmit, isLoading }: ProcessCostSupportFormProps) => {
  const initialValues = {
    hasRealEstate: pcs.hasRealEstate,
    realEstate1Size: pcs.realEstate1Size,
    realEstate1Location: pcs.realEstate1Location,
    realEstate1Ownership: pcs.realEstate1Ownership,
    realEstate1NumberApartments: pcs.realEstate1NumberApartments,
    realEstate1Value: pcs.realEstate1Value,
  };

  const { values, errors, handleChange, handleSubmit, handleBlur, registerValidators, touchedValues } = useForm({
    initialValues,
    onSubmit,
    identifier: pcs,
  });

  return (
    <>
      <Box sx={processCostSupportStyle.sectionHeadline}>Grundeigentum</Box>
      <ValidatorForm onSubmit={handleSubmit}>
        <Grid container sx={processCostSupportStyle.grid}>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Besitzen Sie Grundeigentum?
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Grundeigentum"}
              name={"hasRealEstate"}
              value={formValue(values, "hasRealEstate")}
              onChange={handleChange}
              onBlur={handleBlur}
              registerValidators={registerValidators}
              dependentValidationFields={[
                "realEstate1Size",
                "realEstate1Location",
                "realEstate1Ownership",
                "realEstate1NumberApartments",
                "realEstate1Value",
              ]}
            >
              <MenuItem value={"no"}>Nein</MenuItem>
              <MenuItem value={"yes"}>Ja</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid sx={processCostSupportStyle.question} size={12}>
            Bitte geben Sie hier weitere Details zu Ihrem Grundeigentum an.
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              id="realEstate1Size"
              name="realEstate1Size"
              label="Größe in Quadratmetern"
              onBlur={handleBlur}
              validators={formValue(values, "hasRealEstate") === "yes" ? [...requiredFieldDefault] : []}
              registerValidators={registerValidators}
              error={!!errors["realEstate1Size"]}
              helperText={errors["realEstate1Size"]}
              margin="normal"
              value={formValue(values, "realEstate1Size")}
              onChange={handleChange}
              disabled={formValue(values, "hasRealEstate") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              id="realEstate1Location"
              name="realEstate1Location"
              label="Anschrift/Grundbuchbezeichnung"
              onBlur={handleBlur}
              validators={
                formValue(values, "hasRealEstate") === "yes" ? [...requiredFieldDefault, ...textFieldDefault] : []
              }
              registerValidators={registerValidators}
              error={!!errors["realEstate1Location"]}
              helperText={errors["realEstate1Location"]}
              margin="normal"
              value={formValue(values, "realEstate1Location")}
              onChange={handleChange}
              disabled={formValue(values, "hasRealEstate") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorSelect
              label={"Allein- oder Miteigentum"}
              onBlur={handleBlur}
              error={!!errors["realEstate1Ownership"]}
              helperText={errors["realEstate1Ownership"]}
              fullWidth
              registerValidators={registerValidators}
              validators={formValue(values, "hasRealEstate") === "yes" ? requiredFieldDefault : []}
              value={formValue(values, "realEstate1Ownership")}
              onChange={handleChange}
              name={`realEstate1Ownership`}
              inputProps={{
                name: `realEstate1Ownership`,
                id: "realEstate1Ownership",
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: { maxHeight: 200 },
                  },
                },
              }}
              disabled={formValue(values, "hasRealEstate") !== "yes"}
            >
              <MenuItem value={"Alleineigentum"}>Alleineigentum</MenuItem>
              <MenuItem value={"Miteigentum"}>Miteigentum</MenuItem>
            </ValidatorSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorTextField
              fullWidth={true}
              id="realEstate1NumberApartments"
              name="realEstate1NumberApartments"
              label="Zahl der Wohneinheiten"
              onBlur={handleBlur}
              validators={
                formValue(values, "hasRealEstate") === "yes" ? [...requiredFieldDefault, ...intFieldDefault] : []
              }
              registerValidators={registerValidators}
              error={!!errors["realEstate1NumberApartments"]}
              helperText={errors["realEstate1NumberApartments"]}
              margin="normal"
              value={formValue(values, "realEstate1NumberApartments")}
              onChange={handleChange}
              disabled={formValue(values, "hasRealEstate") !== "yes"}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
              lg: 4,
            }}
          >
            <ValidatorNumberField
              fieldType={"money"}
              numberType={"float"}
              fullWidth={true}
              id="realEstate1Value"
              name="realEstate1Value"
              label="Verkehrswert"
              onBlur={handleBlur}
              isHighlighted={formValue(values, "hasRealEstate") === "yes"}
              registerValidators={registerValidators}
              error={!!errors["realEstate1Value"]}
              helperText={errors["realEstate1Value"]}
              margin="normal"
              value={formValue(values, "realEstate1Value")}
              onChange={handleChange}
              disabled={formValue(values, "hasRealEstate") !== "yes"}
            />
          </Grid>
          <Grid size={12}>
            <SubmitButton
              error={_.keys(errors).length > 0}
              isLoading={isLoading}
              disabled={_.keys(touchedValues).length === 0}
              variant={"contained"}
              type={"submit"}
            >
              Änderungen speichern
            </SubmitButton>
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  );
};

export default RealEstate;
